import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faCartPlus, faPen, faHeart } from '@fortawesome/free-solid-svg-icons';
import { faHeart as faHeartRegular } from '@fortawesome/free-regular-svg-icons';
import './Product.css';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, analytics } from '../../config/firebase';
import { logEvent } from 'firebase/analytics';
import { useFavorites } from '../../hooks/useFavorites';
import ProductSkeleton from './ProductSkeleton';

const Product = ({
    uuid,
    image,
    name,
    provider,
    stars,
    prices,
    favorites,
    isInactive = false,
    privado = false,
    createdAt,
    disableClick = false,
}) => {
    const navigate = useNavigate();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    
    // Usar el hook centralizado de favoritos
    const { isFavorite: isFavoriteGlobal, toggleFavorite } = useFavorites();

    const usuario = JSON.parse(localStorage.getItem('usuario'));
    const userId = usuario?.id;
    const userEmpresa = usuario?.proveedorInfo?.nombre_empresa;
    const isOwnProduct = userEmpresa && provider && userEmpresa === provider;
    const effectiveFavorites = favorites ?? !isOwnProduct;

    // Usar el estado global de favoritos
    const isFavorite = effectiveFavorites ? isFavoriteGlobal(uuid) : false;

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setIsLoggedIn(!!user);
        });

        return () => unsubscribe();
    }, []);

    const handleToggleFavorite = async (e) => {
        e.stopPropagation();
        if (!userId) return;
        
        // Usar la función del hook centralizado
        await toggleFavorite(uuid);
    };

    const handleImageLoad = () => {
        setImageLoaded(true);
    };

    const handleImageError = () => {
        setImageLoaded(true); // Consider loaded even if error to stop skeleton
    };

    const handleClick = () => {
        // Si está deshabilitado, no hacer nada
        if (disableClick) return;
        
        // Track product view in Google Analytics
        if (analytics) {
            logEvent(analytics, 'view_item', {
                currency: 'COP',
                value: parseFloat(mainPrice.amount),
                items: [{
                    item_id: uuid,
                    item_name: name,
                    item_brand: provider,
                    price: parseFloat(mainPrice.amount),
                    item_category: privado ? 'Catálogo Privado' : 'Catálogo Público',
                }]
            });
        }
        
        const path = privado ? `/product/${uuid}?privado=true` : `/product/${uuid}`;
        navigate(path);
    };

    const handleAddToCartClick = (e) => {
        e.stopPropagation();
        sessionStorage.setItem('scrollToQuantity', 'true');
        const path = isOwnProduct ? `/edit-product/${uuid}` : `/product/${uuid}`;
        navigate(path);
    };

    const mainPrice = prices[0];

    const isNew = (() => {
        if (!createdAt) return false;
        const created = new Date(createdAt);
        const now = new Date();
        const diffInMs = now - created;
        const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
        return diffInDays <= 5;
    })();


    return (
        <>
            {!imageLoaded && <ProductSkeleton />}
            <div 
                className={`product ${!imageLoaded ? 'product-hidden' : 'product-visible'} ${disableClick ? 'product-no-click' : ''}`} 
                onClick={handleClick}
                style={{ cursor: disableClick ? 'default' : 'pointer' }}
            >
                {!usuario?.proveedorInfo && effectiveFavorites && !privado && isLoggedIn && (
                    <button
                        className={`add-to-favorites-button ${isFavorite ? 'favorited' : ''}`}
                        onClick={handleToggleFavorite}
                        title={isFavorite ? 'Eliminar de favoritos' : 'Agregar a favoritos'}
                    >
                        <FontAwesomeIcon
                            icon={isFavorite ? faHeart : faHeartRegular}
                            className="heart-icon"
                        />
                    </button>
                )}

                <div className="product-image-wrapper">
                    <img
                        src={image}
                        alt={name}
                        className={`product-image ${isInactive ? 'disabled' : ''}`}
                        loading="lazy"
                        onLoad={handleImageLoad}
                        onError={handleImageError}
                        style={{ 
                            opacity: imageLoaded ? 1 : 0,
                            transition: 'opacity 0.3s ease'
                        }}
                    />
                </div>

                {isNew &&
                    <div className='product-nuevo'>
                        <p className='product-nuevo-span'>NEW</p>
                        <div className='div-1'></div>
                        <div className='div-2'></div>
                        <div className='div-3'></div>
                        <div className='div-4'></div>
                        <div className='div-5'></div>
                    </div>
                }

                <div className='product-info'>
                    <p className="product-name">{name}</p>

                    <div className="product-rating">
                        {[...Array(stars)].map((_, index) => (
                            <FontAwesomeIcon key={index} icon={faStar} className="star-icon" />
                        ))}
                        <span className="rating-text">{provider}</span>
                    </div>
                    <div className='product-price-inf'>
                        <div className="product-price-highlight">
                            <p className="main-price"><span>COP</span>{parseFloat(mainPrice.amount).toLocaleString('es-CO')}</p>
                            <p className="price-note">{mainPrice.condition}</p>
                        </div>
                        <button className="add-to-cart-button" onClick={handleAddToCartClick}>
                            <FontAwesomeIcon
                                icon={isOwnProduct ? faPen : faCartPlus}
                                className={isOwnProduct ? 'edit-icon' : ''}
                            />
                        </button>
                    </div>
                    <div className="tooltip-wrapper">
                        {(prices.length > 1) && (
                            <>
                                <span className="price-more-info">+{prices.length - 1} precios disponibles</span>
                                <div className="tooltip-content">
                                    {prices.slice(1).map((price, i) => (
                                        <p key={i}><strong>$ {parseFloat(price.amount).toLocaleString('es-CO')}</strong> {price.condition}</p>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Product;
